<template>
  <div class="content">
    <!-- Bandeau recette : suppression BDC activee -->
    <v-alert
      v-if="allowBdcDelete"
      type="warning"
      icon="mdi-alert-circle-outline"
      prominent
      dense
      class="mb-3"
      style="font-size: 13px"
    >
      <strong>Environnement de test - suppression BDC activée</strong><br>
      Les bons de commande V2 peuvent être supprimés pour permettre aux testeurs de recommencer un enrôlement. Les données V1 legacy restent protégées.
    </v-alert>

    <!-- Stats V2 -->
    <v-card v-if="stats" elevation="0" outlined>
      <v-card-text>
        <div class="row">
          <div class="col">
            BDC V2 :
            <span style="color: black; font-weight: bold">{{ stats.bdcTotal }}</span>
            <br />
            Signés aujourd'hui :
            <span style="color: black; font-weight: bold">{{ stats.bdcToday }}</span>
            <br />
            V2 : <strong>{{ stats.nbV2 }}</strong> / V1V12 : <strong>{{ stats.nbV1V12 }}</strong>
          </div>
          <div class="col">
            Soumis ASP :
            <span style="color: black; font-weight: bold">{{ stats.bdcSoumisAsp }}</span>
            <br />
            Rejetés :
            <span style="color: red; font-weight: bold">{{ stats.bdcRejetes }}</span>
            <br />
            VA générées :
            <span style="color: black; font-weight: bold">{{ stats.bdcVa }}</span>
          </div>
          <div class="col">
            PS actives :
            <span style="color: black; font-weight: bold">{{ stats.psMonitoring }}</span>
            <br />
            PS prêtes :
            <span style="color: black; font-weight: bold">{{ stats.psReady }}</span>
            <br />
            PS soumises :
            <span style="color: black; font-weight: bold">{{ stats.psSubmitted }}</span>
          </div>
        </div>

        <v-divider style="margin: 10px 0"></v-divider>
        <div class="row">
          <div class="col">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px">TOTAL PRESTATION</div>
            <strong>{{ stats.totalPPHT }} € HT</strong>
            <span style="color: green; font-weight: bold; margin-left: 8px">{{ stats.totalPPTTC }} € TTC</span>
          </div>
          <div class="col">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px">AVANCE (40%)</div>
            <strong>{{ stats.avanceHT }} € HT</strong>
            <span style="color: green; margin-left: 8px">{{ stats.avanceTTC }} € TTC</span>
          </div>
          <div class="col">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px">SOLDE (60%)</div>
            <strong>{{ stats.soldeHT }} € HT</strong>
            <span style="color: green; margin-left: 8px">{{ stats.soldeTTC }} € TTC</span>
          </div>
          <div class="col" v-if="stats.psEstimeHT > 0">
            <div style="font-size: 12px; color: #666; margin-bottom: 4px">PREST. SECONDAIRE</div>
            <strong>{{ stats.psEstimeHT }} € HT</strong>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Recherche -->
    <md-card style="margin-bottom: 40px; margin-top: 10px">
      <div class="md-layout">
        <div class="md-layout-item">
          <md-field>
            <label>Recherche (nom, RPPS, N° éditeur, N° ASP)</label>
            <md-input v-on:keyup.enter="search" v-model="query"></md-input>
          </md-field>
        </div>
        <div class="md-layout-item" style="margin-top: 7px">
          <v-select :items="sortOptions" v-model="sortBy" label="Trier par"></v-select>
        </div>
        <div class="md-layout-item" style="margin-top: 7px">
          <v-select :items="['Décroissant', 'Croissant']" v-model="sortOrder" label="Tri"></v-select>
        </div>
      </div>
      <md-button class="md-prokov researcheButton" @click="search()">Rechercher</md-button>
      <button class="diagCloseButton" @click="clearSearch()">
        <md-icon>delete</md-icon>
      </button>
    </md-card>

    <!-- Filtres -->
    <v-tabs v-model="currentTab" background-color="prokov" dark slider-color="white" slider-size="3" style="border-radius: 8px; margin-top: 10px">
      <v-tab v-for="t in tabDefs" :key="t.key" :href="'#' + t.key">
        {{ t.label }}
        <span v-if="tabCounts[t.key]" style="background: rgba(255,255,255,0.25); color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 10px; margin-left: 6px">
          {{ tabCounts[t.key] }}
        </span>
      </v-tab>
    </v-tabs>

    <!-- Tableau BDC (tous les onglets sauf PS) -->
    <v-tabs-items v-model="currentTab" style="background: rgba(0, 0, 0, 0)">
      <v-tab-item v-for="t in tabDefs.filter(t => t.key !== 'ps')" :key="t.key" :value="t.key">
        <div style="width: 100%" class="md-layout">
          <md-table v-if="rows && rows.length" v-model="rows">
            <md-table-row slot="md-table-row" md-selectable="single" slot-scope="{ item }">

              <md-table-cell md-label="Résident" v-if="item.resident">
                {{ item.resident.prenom }} {{ item.resident.nom }}
              </md-table-cell>

              <md-table-cell md-label="RPPS">
                <template v-if="item.msp">F - {{ item.msp.FINESS }}</template>
                <template v-else-if="item.resident">R - {{ item.resident.RPPS }}</template>
              </md-table-cell>

              <md-table-cell md-label="N° Éditeur">{{ item.numeroEditeur }}</md-table-cell>

              <md-table-cell md-label="Type">
                <v-chip v-if="item.typePrestation" x-small :color="typeColor(item.typePrestation)" dark>
                  {{ item.typePrestation }}
                </v-chip>
                <span v-else-if="item.wave === 'V1'" style="font-size: 12px">V1</span>
                <span v-else>-</span>
              </md-table-cell>

              <md-table-cell md-label="Montant">{{ item.montantSegurHT }} €</md-table-cell>

              <md-table-cell md-label="Date signature">
                {{ getDate(item.dateSignatureCommande || item.createdAt) }}
              </md-table-cell>

              <md-table-cell md-label="État">
                <v-chip x-small :color="stateColor(item.state)" dark>{{ stateLabel(item.state) }}</v-chip>
              </md-table-cell>

              <md-table-cell md-label="Pilote" v-if="currentTab !== 'archivesV1'">
                <v-chip v-if="item.isPilot" x-small :color="pilotColor(item.pilotStatus)" dark>
                  {{ pilotLabel(item.pilotStatus) }}
                </v-chip>
                <span v-else style="color: #ccc">-</span>
              </md-table-cell>

              <md-table-cell md-label="N° ASP">{{ item.numeroASP || '-' }}</md-table-cell>

              <md-table-cell md-label="VA">
                <v-chip v-if="item.files && item.files.vaPDF" color="green" x-small dark>Oui</v-chip>
                <v-chip v-else color="red" x-small dark>Non</v-chip>
              </md-table-cell>

              <md-table-cell md-label="PS" v-if="currentTab !== 'archivesV1'">
                <v-chip v-if="item.ps" x-small :color="psColor(item.ps.status)" dark>
                  {{ item.ps.status }}
                </v-chip>
                <span v-else>-</span>
              </md-table-cell>

              <template v-if="currentTab === 'rejetes'">
                <md-table-cell md-label="Code">{{ item.codeTraitement }}</md-table-cell>
                <md-table-cell md-label="Libellé">{{ item.libelleTraitement }}</md-table-cell>
              </template>

              <md-table-cell md-label="">
                <v-btn class="mx-2" icon small color="teal" @click="openDetail(item)">
                  <v-icon dark>mdi-format-list-bulleted-square</v-icon>
                </v-btn>
              </md-table-cell>

            </md-table-row>
          </md-table>
          <div v-else style="padding: 20px; text-align: center; color: #999">
            Aucun bon de commande
          </div>
        </div>

        <div class="text-center" style="margin-top: 10px">
          <v-pagination v-model="page" :length="pageCount"></v-pagination>
        </div>
      </v-tab-item>

      <!-- Onglet Prestation Secondaire (tableau dedie) -->
      <v-tab-item key="ps" value="ps">
        <div style="display: flex; gap: 24px; align-items: center; flex-wrap: wrap; padding: 8px 4px; font-size: 11px; color: #888">
          <span>PERF :
            <span style="color: green">● ≥60% = 1.0</span>
            <span style="color: orange; margin-left: 6px">● 40-59% = 0.5</span>
            <span style="color: red; margin-left: 6px">● &lt;40% = 0</span>
          </span>
          <span style="border-left: 1px solid #ddd; padding-left: 12px">
            <strong>Dt</strong> docs attendus
            <strong style="margin-left: 8px">Dm</strong> docs envoyés
            <strong style="margin-left: 8px">T</strong> patients
            <strong style="margin-left: 8px">Tq</strong> INS qualifiée
            <strong style="margin-left: 8px">Ts</strong> INS transitoire
          </span>
        </div>
        <div style="width: 100%" class="md-layout">
          <md-table v-if="psRows && psRows.length" v-model="psRows">
            <md-table-row slot="md-table-row" slot-scope="{ item }">

              <md-table-cell md-label="Résident" v-if="item.resident">
                {{ item.resident.prenom }} {{ item.resident.nom }}
              </md-table-cell>

              <md-table-cell md-label="RPPS" v-if="item.resident">
                {{ item.resident.RPPS }}
              </md-table-cell>

              <md-table-cell md-label="N° Éditeur">{{ item.numeroEditeur }}</md-table-cell>

              <md-table-cell md-label="Début">{{ item.dateDebut }}</md-table-cell>
              <md-table-cell md-label="Fin">{{ item.dateFin }}</md-table-cell>

              <md-table-cell md-label="Status">
                <v-chip x-small :color="psColor(item.status)" dark>{{ item.status }}</v-chip>
              </md-table-cell>

              <md-table-cell md-label="PERF">
                <strong v-if="item.perfCoefficient != null" :style="{ color: item.perfCoefficient >= 1 ? 'green' : item.perfCoefficient > 0 ? 'orange' : 'red' }">
                  {{ item.perfCoefficient }}
                </strong>
                <strong v-else :style="{ color: item.dmDtRatio >= 60 ? 'green' : item.dmDtRatio >= 40 ? 'orange' : 'red' }">
                  {{ item.dmDtRatio >= 60 ? '1.0' : item.dmDtRatio >= 40 ? '0.5' : '0' }}
                </strong>
              </md-table-cell>

              <md-table-cell md-label="Progression" style="min-width: 140px">
                <div style="display: flex; align-items: center; gap: 8px">
                  <v-progress-linear
                    :value="item.dmDtRatio"
                    :color="item.dmDtRatio >= 60 ? 'green' : item.dmDtRatio >= 40 ? 'orange' : 'red'"
                    height="12"
                    rounded
                    style="max-width: 80px"
                  ></v-progress-linear>
                  <span style="font-size: 12px; font-weight: bold">{{ item.dmDtRatio }}%</span>
                </div>
              </md-table-cell>

              <md-table-cell md-label="Dt">{{ item.dt }}</md-table-cell>
              <md-table-cell md-label="Dm">{{ item.dm }}</md-table-cell>
              <md-table-cell md-label="T">{{ item.t }}</md-table-cell>
              <md-table-cell md-label="Tq">{{ item.tq }}</md-table-cell>
              <md-table-cell md-label="Ts">{{ item.ts }}</md-table-cell>

            </md-table-row>
          </md-table>
          <div v-else style="padding: 20px; text-align: center; color: #999">
            Aucune prestation secondaire
          </div>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- Detail dialog -->
    <v-dialog v-model="dialog" width="800">
      <v-card v-if="selectedItem">
        <v-card-title class="text-h5 grey lighten-2">
          {{ selectedItem.resident ? selectedItem.resident.prenom + ' ' + selectedItem.resident.nom : 'BDC' }}
        </v-card-title>
        <v-card-text>
          <v-col cols="12">
            <h4>BON DE COMMANDE</h4>
            <div style="color: blue; margin-left: 10px; margin-top: 10px">
              ID : {{ selectedItem.id }}<br />
              N° éditeur : {{ selectedItem.numeroEditeur }} / N° ASP : {{ selectedItem.numeroASP || '-' }}<br />
              Montant HT : {{ selectedItem.montantSegurHT }} € / Type : {{ selectedItem.typePrestation }}<br />
              État : {{ selectedItem.state }} / Wave : {{ selectedItem.wave }}<br />
              Partenaire : {{ selectedItem.partnerCode }}<br />
              <span v-if="selectedItem.isPilot">
                Pilote : <v-chip x-small :color="pilotColor(selectedItem.pilotStatus)" dark>{{ pilotLabel(selectedItem.pilotStatus) }}</v-chip><br />
              </span>
              <br />
              <div style="display: flex; gap: 8px; flex-wrap: wrap; margin: 4px 0">
                <v-btn v-if="selectedItem.files && selectedItem.files.orderFormPDF" x-small color="primary" @click="downloadPdf('bdc', selectedItem.id)">
                  <v-icon x-small left>mdi-download</v-icon> BDC
                </v-btn>
                <v-btn v-if="selectedItem.files && selectedItem.files.vaPDF" x-small color="green" dark @click="downloadPdf('va', selectedItem.id)">
                  <v-icon x-small left>mdi-download</v-icon> VA
                </v-btn>
                <v-btn v-if="selectedItem.files && selectedItem.files.invoicePDF" x-small color="orange" dark @click="downloadPdf('fac', selectedItem.id)">
                  <v-icon x-small left>mdi-download</v-icon> Facture
                </v-btn>
                <v-btn v-if="selectedItem.files && selectedItem.files.momPDF" x-small color="grey" dark @click="downloadPdf('mom', selectedItem.id)">
                  <v-icon x-small left>mdi-download</v-icon> MOM
                </v-btn>
                <span v-if="!(selectedItem.files && (selectedItem.files.orderFormPDF || selectedItem.files.vaPDF || selectedItem.files.invoicePDF || selectedItem.files.momPDF))" style="color: #999">
                  Aucun PDF disponible
                </span>
              </div>
              <br />
              <span v-if="selectedItem.codeTraitement" style="color: green">
                Retour ASP : {{ selectedItem.codeTraitement }} / {{ selectedItem.libelleTraitement }}<br />
              </span>
            </div>

            <div v-if="selectedItem.resident" style="margin-top: 20px">
              <h4>RÉSIDENT</h4>
              <div style="color: blue; margin-left: 10px; margin-top: 10px">
                {{ selectedItem.resident.nom }} {{ selectedItem.resident.prenom }}<br />
                RPPS : {{ selectedItem.resident.RPPS }}<br />
                Email : {{ selectedItem.resident.email }}<br />
                Tél : {{ selectedItem.resident.phone }}<br />
              </div>
            </div>

            <div v-if="selectedItem.msp" style="margin-top: 20px">
              <h4>MSP</h4>
              <div style="color: blue; margin-left: 10px; margin-top: 10px">
                FINESS : {{ selectedItem.msp.FINESS }}<br />
              </div>
            </div>

            <div v-if="selectedItem.ps" style="margin-top: 20px">
              <h4>PRESTATION SECONDAIRE</h4>
              <div style="color: blue; margin-left: 10px; margin-top: 10px">
                Status : {{ selectedItem.ps.status }}<br />
                PERF : {{ selectedItem.ps.perfCoefficient != null ? selectedItem.ps.perfCoefficient : '-' }}<br />
                Période : {{ selectedItem.ps.dateDebut }} au {{ selectedItem.ps.dateFin }}<br />
                Dt : {{ selectedItem.ps.dt }} / Dm : {{ selectedItem.ps.dm }}<br />
              </div>
            </div>
          </v-col>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <!-- Bouton supprimer (recette uniquement, V2 uniquement) -->
          <v-btn
            v-if="allowBdcDelete && selectedItem && selectedItem.wave !== 'V1'"
            x-small
            color="red"
            dark
            @click="showDeleteConfirmDialog = true"
          >
            <v-icon x-small left>mdi-delete-outline</v-icon>
            Supprimer
          </v-btn>
          <v-btn v-if="selectedItem && !selectedItem.isPilot && selectedItem.state !== 'OFF'" x-small color="purple" dark @click="togglePilot(selectedItem, true)">
            Marquer pilote
          </v-btn>
          <v-btn v-if="selectedItem && selectedItem.isPilot && selectedItem.pilotStatus !== 'VALIDATED'" x-small color="grey" dark @click="togglePilot(selectedItem, false)">
            Retirer pilote
          </v-btn>
          <v-btn v-if="selectedItem && selectedItem.isPilot && selectedItem.pilotStatus === 'NONE'" x-small color="orange" dark @click="updatePilotStatus(selectedItem, 'SUBMITTED')">
            Soumis ASP
          </v-btn>
          <v-btn v-if="selectedItem && selectedItem.isPilot && selectedItem.pilotStatus === 'SUBMITTED'" x-small color="green" dark @click="updatePilotStatus(selectedItem, 'VALIDATED')">
            Valide ASP
          </v-btn>
          <v-btn v-if="selectedItem && selectedItem.isPilot && selectedItem.pilotStatus === 'SUBMITTED'" x-small color="red" dark @click="updatePilotStatus(selectedItem, 'REJECTED')">
            Rejete ASP
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">Ok</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmation de suppression BDC (recette uniquement) -->
    <v-dialog v-model="showDeleteConfirmDialog" persistent max-width="460">
      <v-card>
        <v-card-title class="headline" style="color: #d32f2f">
          <v-icon left color="red">mdi-alert-circle-outline</v-icon>
          Supprimer ce bon de commande ?
        </v-card-title>
        <v-card-text style="font-size: 13px">
          <strong>Action irréversible</strong><br /><br />
          Le bon de commande suivant va être supprimé, ainsi que tous ses documents PDF et ses données de suivi PS.
          <div style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin: 12px 0">
            <template v-if="selectedItem">
              N° Éditeur : <strong>{{ selectedItem.numeroEditeur }}</strong><br />
              <template v-if="selectedItem.resident">
                Praticien : <strong>{{ selectedItem.resident.prenom }} {{ selectedItem.resident.nom }}</strong><br />
                RPPS : {{ selectedItem.resident.RPPS }}<br />
              </template>
              <template v-if="selectedItem.msp">
                FINESS : {{ selectedItem.msp.FINESS }}<br />
              </template>
              Montant HT : <strong>{{ selectedItem.montantSegurHT }} €</strong><br />
              État : <strong>{{ selectedItem.state }}</strong><br />
              Wave : <strong>{{ selectedItem.wave }}</strong>
            </template>
          </div>
          Les données du médecin (résident) et de la structure (MSP) ne sont pas supprimées : le médecin peut recommencer un enrôlement avec sa CPS.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showDeleteConfirmDialog = false">Annuler</v-btn>
          <v-btn color="red" dark @click="confirmDeleteBDC">
            <v-icon left small>mdi-delete</v-icon>
            Supprimer définitivement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import server from "@/store/server";

const API_BASE = server.url;
const PAGE_SIZE = 50;

export default {
  name: "SegurV2",
  data() {
    return {
      stats: null,
      apiBase: API_BASE,
      rows: [],
      psRows: [],
      totalCount: 0,
      tabCounts: {},
      currentTab: "all",
      query: "",
      sortBy: "N° Éditeur",
      sortOrder: "Décroissant",
      sortOptions: ["N° Éditeur", "N° ASP", "Montant", "Date", "État"],
      page: 1,
      dialog: false,
      selectedItem: null,
      showDeleteConfirmDialog: false,
    };
  },
  computed: {
    // Feature recette uniquement : suppression de BDC pour re-enrolement testeur
    // Le backend a sa propre garde (ALLOW_BDC_DELETE), le frontend masque juste le bouton
    allowBdcDelete() {
      return process.env.VUE_APP_ALLOW_BDC_DELETE === "true";
    },
    tabDefs() {
      return [
        { key: "all", label: "Tous" },
        { key: "enCours", label: "En cours" },
        { key: "soumisAsp", label: "Soumis ASP" },
        { key: "rejetes", label: "Rejetés" },
        { key: "va", label: "VA" },
        { key: "pilots", label: "Pilotes" },
        { key: "ps", label: "Prest. Secondaire" },
      ];
    },
    pageCount() {
      return Math.ceil(this.totalCount / PAGE_SIZE) || 1;
    },
  },
  watch: {
    currentTab() {
      this.page = 1;
      if (this.currentTab === "ps") {
        this.fetchPS();
      } else {
        this.fetchOrderForms();
      }
    },
    page() {
      this.fetchOrderForms();
    },
  },
  async created() {
    await Promise.all([this.fetchStats(), this.fetchOrderForms()]);
  },
  methods: {
    async fetchStats() {
      try {
        const res = await axios.get(`${API_BASE}/segur/v2/stats`, { headers: server.getHeader() });
        if (res.data && res.data.success) {
          this.stats = res.data.data;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Segur] Erreur stats:", err.message);
      }
    },

    async fetchOrderForms() {
      try {
        const sortMap = {
          "N° Éditeur": "numeroEditeur",
          "N° ASP": "numeroASP",
          "Montant": "montant",
          "Date": "date",
          "État": "state",
        };
        const res = await axios.post(
          `${API_BASE}/segur/v2/orderforms`,
          {
            tab: this.currentTab,
            search: this.query,
            sortBy: sortMap[this.sortBy] || "numeroEditeur",
            sortOrder: this.sortOrder === "Croissant" ? "ASC" : "DESC",
            offset: (this.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
          { headers: server.getHeader() }
        );
        if (res.data && res.data.success) {
          this.rows = res.data.data.rows;
          this.totalCount = res.data.data.count;
          this.tabCounts = res.data.data.tabs;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Segur] Erreur listing:", err.message);
      }
    },

    async fetchPS() {
      try {
        const res = await axios.get(
          `${API_BASE}/segur/v2/prestations-secondaires`,
          { headers: server.getHeader() }
        );
        if (res.data && res.data.success) {
          this.psRows = res.data.data;
          this.tabCounts = { ...this.tabCounts, ps: res.data.data.length };
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Segur] Erreur PS:", err.message);
      }
    },

    async downloadPdf(type, id) {
      try {
        const res = await axios.get(
          `${API_BASE}/segur/download/${type}/${id}`,
          { headers: server.getHeader(), responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${type}-${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Segur] Erreur téléchargement PDF:", err.message);
      }
    },

    search() {
      this.page = 1;
      this.fetchOrderForms();
    },

    clearSearch() {
      this.query = "";
      this.page = 1;
      this.fetchOrderForms();
    },

    openDetail(item) {
      this.selectedItem = item;
      this.dialog = true;
    },

    getDate(d) {
      if (!d) return "";
      const date = new Date(d);
      return (
        ("0" + date.getDate()).slice(-2) +
        "/" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "/" +
        date.getFullYear()
      );
    },

    stateLabel(state) {
      if (state === "ON") return "Terminé";
      if (state === "ENABLE") return "Signé";
      if (state === "OFF") return "Non signé";
      if (state === "DISABLE") return "Désactivé";
      return state;
    },

    stateColor(state) {
      if (state === "ON") return "green";
      if (state === "ENABLE") return "orange";
      if (state === "DISABLE") return "grey";
      return "red";
    },

    typeColor(type) {
      if (type === "V2") return "blue";
      if (type === "V1V12") return "orange";
      return "grey";
    },

    psColor(status) {
      if (status === "APPROVED") return "green";
      if (status === "READY") return "blue";
      if (status === "MONITORING") return "orange";
      if (status === "REJECTED") return "red";
      return "grey";
    },

    pilotColor(status) {
      if (status === "VALIDATED") return "green";
      if (status === "SUBMITTED") return "orange";
      if (status === "REJECTED") return "red";
      return "purple";
    },

    pilotLabel(status) {
      if (status === "VALIDATED") return "Validé";
      if (status === "SUBMITTED") return "Soumis";
      if (status === "REJECTED") return "Rejeté";
      return "Pilote";
    },

    async togglePilot(item, isPilot) {
      try {
        await axios.patch(
          `${this.apiBase}/segur/v2/orderforms/${item.id}/pilot`,
          { isPilot },
          { headers: server.getHeader() }
        );
        this.dialog = false;
        this.fetchOrderForms();
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        alert(msg);
      }
    },

    async updatePilotStatus(item, pilotStatus) {
      try {
        await axios.patch(
          `${this.apiBase}/segur/v2/orderforms/${item.id}/pilot-status`,
          { pilotStatus },
          { headers: server.getHeader() }
        );
        this.dialog = false;
        this.fetchOrderForms();
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        alert(msg);
      }
    },

    // Suppression BDC (recette uniquement, backend a sa propre garde ALLOW_BDC_DELETE)
    async confirmDeleteBDC() {
      if (!this.selectedItem) return;
      try {
        await axios.delete(
          `${this.apiBase}/segur/v2/orderforms/${this.selectedItem.id}`,
          { headers: server.getHeader() }
        );
        this.showDeleteConfirmDialog = false;
        this.dialog = false;
        this.selectedItem = null;
        await this.fetchOrderForms();
        await this.fetchStats();
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        alert(`Erreur lors de la suppression : ${msg}`);
      }
    },
  },
};
</script>

<style scoped>
.content {
  max-width: 100%;
  padding: 0;
}

/* Tous les blocs alignes sur la meme largeur */
.content > .v-card,
.content > .md-card,
.content > .v-tabs,
.content > .v-tabs-items {
  margin-left: 0;
  margin-right: 0;
}

/* Tableau pleine largeur */
.md-table {
  width: 100%;
}

.md-table .md-table-cell {
  white-space: nowrap;
  font-size: 13px;
  padding: 4px 8px;
}

/* Chips plus compacts */
.v-chip.v-size--x-small {
  font-size: 11px;
  height: 20px;
}

/* Tabs filtres : texte blanc, actif en blanc gras (pas de bleu) */
.v-tabs .v-tab {
  color: rgba(255, 255, 255, 0.7) !important;
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 0.5px;
}
.v-tabs .v-tab--active {
  color: #fff !important;
  font-weight: bold;
}
</style>
